import React, { useEffect, useRef } from 'react';

interface PhraseGridProps {
  phrase: string;
  revealedPositions: Set<number>;
  userInputs: string[];
  isWon: boolean;
  isRevealed: boolean;
  onInputChange: (slotIndex: number, char: string) => void;
  onBackspace: (slotIndex: number) => void;
  onNavigate: (slotIndex: number, direction: 'left' | 'right') => void;
  onSubmit: () => void;
}

export const PhraseGrid: React.FC<PhraseGridProps> = ({
  phrase,
  revealedPositions,
  userInputs,
  isWon,
  isRevealed,
  onInputChange,
  onBackspace,
  onNavigate,
  onSubmit,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const upperPhrase = phrase.toUpperCase();
  const words = upperPhrase.split(' ');

  let charGlobalIdx = 0;
  let letterSlotCount = 0;

  // Auto-focus first editable slot on mount or round change
  useEffect(() => {
    if (!isWon && !isRevealed) {
      // Find first unrevealed slot
      const firstEditable = inputRefs.current.findIndex((ref) => ref && !ref.disabled);
      if (firstEditable !== -1 && inputRefs.current[firstEditable]) {
        inputRefs.current[firstEditable]?.focus();
      }
    }
  }, [phrase, isWon, isRevealed]);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    slotIdx: number
  ) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      onBackspace(slotIdx);
      // Focus previous editable input if current was empty
      if (!userInputs[slotIdx] || userInputs[slotIdx] === '') {
        let prev = slotIdx - 1;
        while (prev >= 0 && inputRefs.current[prev]?.disabled) {
          prev--;
        }
        if (prev >= 0 && inputRefs.current[prev]) {
          inputRefs.current[prev]?.focus();
        }
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      let prev = slotIdx - 1;
      while (prev >= 0 && inputRefs.current[prev]?.disabled) {
        prev--;
      }
      if (prev >= 0 && inputRefs.current[prev]) {
        inputRefs.current[prev]?.focus();
      }
      onNavigate(slotIdx, 'left');
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      let next = slotIdx + 1;
      while (next < inputRefs.current.length && inputRefs.current[next]?.disabled) {
        next++;
      }
      if (next < inputRefs.current.length && inputRefs.current[next]) {
        inputRefs.current[next]?.focus();
      }
      onNavigate(slotIdx, 'right');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    slotIdx: number
  ) => {
    const rawVal = e.target.value.toUpperCase();
    // Extract last typed character if any
    const lastChar = rawVal.replace(/[^A-Z]/g, '').slice(-1);

    if (lastChar) {
      onInputChange(slotIdx, lastChar);

      // Auto-advance to next editable input
      let next = slotIdx + 1;
      while (next < inputRefs.current.length && inputRefs.current[next]?.disabled) {
        next++;
      }
      if (next < inputRefs.current.length && inputRefs.current[next]) {
        inputRefs.current[next]?.focus();
      }
    } else if (e.target.value === '') {
      onInputChange(slotIdx, '');
    }
  };

  return (
    <div className="w-full flex flex-wrap justify-center items-center gap-y-4 gap-x-6 py-6 px-2 my-2 min-h-[120px]">
      {words.map((word, wordIdx) => {
        const wordElements = (
          <div key={wordIdx} className="flex items-center gap-1.5 flex-nowrap">
            {word.split('').map((char) => {
              const currentGlobalIdx = charGlobalIdx++;
              const isLetter = /[A-Z]/.test(char);

              if (!isLetter) {
                // Non-letter punctuation (hyphen, apostrophe)
                return (
                  <div
                    key={currentGlobalIdx}
                    className="text-2xl font-bold text-indigo-300 px-1 flex items-center justify-center select-none"
                  >
                    {char}
                  </div>
                );
              }

              const slotIdx = letterSlotCount++;
              const isHintRevealed = revealedPositions.has(currentGlobalIdx);
              const userTyped = userInputs[slotIdx] || '';
              const isDisabled = isWon || isRevealed || isHintRevealed;
              const displayChar = isWon || isRevealed || isHintRevealed ? char : userTyped;

              return (
                <input
                  key={currentGlobalIdx}
                  ref={(el) => (inputRefs.current[slotIdx] = el)}
                  type="text"
                  maxLength={2}
                  value={displayChar}
                  disabled={isDisabled}
                  onChange={(e) => handleChange(e, slotIdx)}
                  onKeyDown={(e) => handleKeyDown(e, slotIdx)}
                  className={`w-10 h-13 sm:w-12 sm:h-15 rounded-xl text-center font-extrabold text-xl sm:text-2xl transition-all duration-200 transform select-none caret-transparent focus:outline-none ${
                    isHintRevealed
                      ? 'bg-amber-500/20 border-2 border-amber-400 text-amber-300 shadow-md shadow-amber-500/20 scale-105 cursor-default'
                      : isWon
                      ? 'bg-emerald-600/30 border-2 border-emerald-400 text-white shadow-md shadow-emerald-500/20 scale-105 cursor-default'
                      : isRevealed
                      ? 'bg-rose-500/20 border-2 border-rose-500 text-rose-300 cursor-default'
                      : userTyped
                      ? 'bg-indigo-600/30 border-2 border-indigo-400 text-white shadow-md shadow-indigo-500/20 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-500/30'
                      : 'bg-slate-900/90 border-b-4 border-indigo-500/80 text-white hover:bg-slate-800 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/30'
                  }`}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                />
              );
            })}
          </div>
        );

        if (wordIdx < words.length - 1) {
          charGlobalIdx++; // space boundary
        }

        return wordElements;
      })}
    </div>
  );
};
